// =============================================================================
// ui/shell/SnapshotButton.tsx — the header camera.
// -----------------------------------------------------------------------------
// 🔴 HIDDEN ON A TAB THAT OFFERS NO CAPTURE, AND THE COMMENT HERE USED TO SAY
// THE OPPOSITE. It claimed the old client "keeps the button present everywhere
// and toasts", and argued from that for greying out instead.
//
// Beta hides it. `updateExportButtons()` (app/index.html:11801-11806) sets
// `style.display = 'none'` on both the share and snapshot buttons off the three
// capturable tabs, under its own comment: "on the tabs they support, so they
// aren't dead buttons everywhere else." The toast at :16057 is a defensive
// fallback for a handler that fires anyway, not the behaviour a user meets.
//
// ⚠ So the divergence was real, undeclared, and justified by a false claim
// about what it was diverging from. #43. Hiding is also the better argument on
// its own terms: a permanently grey button on eleven of fourteen tabs teaches
// that the camera is broken, not that it is contextual.
// =============================================================================

import { useSnapshot } from '../state/snapshot';
import { Icon } from './Icon';

export function SnapshotButton() {
  const { target, phase, capture } = useSnapshot();
  const busy = phase === 'capturing';

  // ⚠ Nothing at all, rather than a disabled control. A tab opts in by
  // registering a region; none has, so there is nothing here to press.
  if (!target) return null;

  return (
    <button
      type="button"
      className="btn"
      onClick={capture}
      disabled={busy}
      /* ⚠ ICON ONLY, AS IN BETA (#51), so the accessible name is the ONLY name
         this control has. It carries the busy state too — a disabled button
         with no text is silent to a screen reader otherwise, and the visible
         "Capturing…" that used to say so is gone with the label. */
      aria-label={busy ? 'Capturing a snapshot' : 'Snapshot this view'}
      aria-busy={busy}
      title={busy ? 'Capturing…' : `Snapshot ${target.label} as a PNG`}
    >
      {/* ⚠ THE OLD CLIENT'S CAMERA PATH, via the shared icon set — this was a
          hand-drawn approximation of it, which is how two clients end up with
          subtly different toolbars that no screenshot diff can explain. */}
      <Icon name="snapshots" />
    </button>
  );
}
